import moment from 'moment';
import { cloneDeep } from 'lodash';
import type { DAYS, TIMES } from '@bb/domain/dist/types/AppointmentConfig';
import type {
  AccessHoursRangeType,
  AccessHoursType,
  DefaultAccessHoursType,
  DKCoreDeviceAccessHoursType,
  SupraLockboxDayLabelType,
} from '@bb/domain/dist/types/SupraLockbox';

/**
 * @param time time string i.e. "10:00"
 * @returns number i.e. 1000
 */
export const timeStrToNumber = (time: string) => +time.replace(':', '');

/**
 * @param time time number i.e. 1000
 * @returns string i.e. "10:00"
 */
export const timeNumToFormattedStr = (time: number) => time.toString().replace(/\b(\d{1,2})(\d{2})/, '$1:$2') as TIMES;

/**
 * @param time Date or datetime string i.e. "2024-01-01T13:30"
 * @returns 24hr time as a number i.e. 1330
 */
export const convertTo24HrTimeNum: (time: string | Date) => number = time => +moment(time).format('Hmm');

/**
 * @param time 24hr time as string with no punctuation i.e. 1330 is 1:30PM
 * @returns Date
 */
export const convertToDatetime: (time: number) => Date = time => {
  const timeStr = time.toString();
  if (timeStr.length <= 2) return new Date(2024, 0, 1, 0, +time);
  if (timeStr.length === 3)
    return new Date(
      2024,
      0,
      1,
      +timeStr.substring(0, 1),
      +timeStr.substring(1),
    );
  return new Date(2024, 0, 1, +timeStr.substring(0, 2), +timeStr.substring(2));
};

export const parseAccessHours = (
  accessHours:
    | Record<string, { startTime: number; endTime: number }>
    | DefaultAccessHoursType
    | undefined,
  lockboxType: string,
  forDisplay?: boolean,
) => {
  if (!accessHours || !Object.keys(accessHours).length) {
    const defaultTimes = {
      startTime: convertToDatetime(0),
      endTime: convertToDatetime(0),
    };
    return {
      saturday: defaultTimes,
      sunday: defaultTimes,
      ...(lockboxType === 'iBox BTLE' || lockboxType === 'iBox'
        ? { monday2Friday: defaultTimes }
        : {
            monday: defaultTimes,
            tuesday: defaultTimes,
            wednesday: defaultTimes,
            thursday: defaultTimes,
            friday: defaultTimes,
          }),
    };
  }
  // handle parsing from customer default hours
  // currently there is no need to parse from customer default for display
  if (Object.keys(accessHours).includes('startTime') && !forDisplay) {
    const { startTime: startTimeNum, endTime: endTimeNum } =
      accessHours as DefaultAccessHoursType;
    const startTime = convertToDatetime(startTimeNum);
    const endTime = convertToDatetime(endTimeNum);
    const hours = {
      startTime,
      endTime,
    };
    const parsedHours = {
      saturday: hours,
      sunday: hours,
      ...(lockboxType === 'iBox'
        ? { monday2Friday: hours }
        : {
            monday: hours,
            tuesday: hours,
            wednesday: hours,
            thursday: hours,
            friday: hours,
          }),
    };
    return parsedHours;
  }
  const dayKeys = [
    'monday2Friday',
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday',
  ];
  return Object.entries(accessHours).reduce(
    (
      acc,
      [day, { startTime: startTimeNum, endTime: endTimeNum }]: [
        SupraLockboxDayLabelType,
        AccessHoursRangeType<number>,
      ],
    ) => {
      if (!dayKeys.includes(day)) return acc;

      const startTime = convertToDatetime(startTimeNum);
      const endTime = convertToDatetime(endTimeNum);

      if (forDisplay) {
        acc![day] = {
          startTime: moment(startTime).format('h:mm A'),
          endTime: moment(endTime).format('h:mm A'),
        };
        return acc as AccessHoursType<string>;
      }

      acc![day[0].toLowerCase() + day.slice(1)] = {
        startTime,
        endTime,
      };
      return acc as AccessHoursType<Date>;
    },
    {} as any,
  );
};

// Updates to this function should also be made to
// LockboxService.getSupraLockboxRestrictedHoursMap
// and _buildLockboxHoursMap in mobile
export const buildLockboxRestrictedHoursMap = (
  supraLockboxAccessHours: Record<
    string,
    { startTime: Date | string; endTime: Date | string }
  >,
) => {
  if (!supraLockboxAccessHours) return {};
  const hours = cloneDeep(supraLockboxAccessHours);

  // standardize data if iBox
  if ({}.hasOwnProperty.call(hours, 'monday2Friday')) {
    hours.monday = hours.monday2Friday;
    hours.tuesday = hours.monday2Friday;
    hours.wednesday = hours.monday2Friday;
    hours.thursday = hours.monday2Friday;
    hours.friday = hours.monday2Friday;
    delete hours.monday2Friday;
  }

  const restrictedTimeMap = Object.entries(hours).reduce(
    (acc, [day, { startTime, endTime }]) => {
      const dayMap: Record<string, DAYS> = {
        monday: 'MON',
        tuesday: 'TUE',
        wednesday: 'WED',
        thursday: 'THU',
        friday: 'FRI',
        saturday: 'SAT',
        sunday: 'SUN',
      };

      if (!Object.keys(dayMap).includes(day)) return acc;

      const firstTimeslot = 700;
      const lastTimeslot = 2400;

      let start = convertTo24HrTimeNum(startTime);
      let end = convertTo24HrTimeNum(endTime);

      // BrokerBay AppointmnetConfig timeslots are in 30min increments and start at 7:00 AM
      // Supra Lockbox Access hours are in 15min increments and range from 0 - 2400 (12:00 AM to 12:00 AM)
      // Adjust Supra start and end time to valid AppointmentConfig timeslot
      start =
        start < 700
          ? 700
          : start % 100 === 15
            ? start + 15
            : start % 100 === 45
              ? start + 55
              : start;

      end =
        end === 0
          ? 2400
          : end < 700
            ? 700
            : end % 100 === 15 || end % 100 === 45
              ? end - 15
              : end;

      let _start = start;
      let _end = end;
      const times: TIMES[] = [];

      const recalculateStart = () => {
        _start = _start % 100 === 30 ? (_start -= 30) : (_start -= 70);
        return _start;
      };
      for (let i = _start; i >= firstTimeslot; i = recalculateStart()) {
        if (i !== start) times.push(timeNumToFormattedStr(i));
      }

      const recalculateEnd = () => {
        _end = _end % 100 === 30 ? (_end += 70) : (_end += 30);
        return _end;
      };
      for (let i = _end; i < lastTimeslot; i = recalculateEnd()) {
        times.push(timeNumToFormattedStr(i));
      }

      acc[dayMap[day]] = times;
      return acc;
    },
    {} as Record<DAYS, TIMES[]>,
  );
  return restrictedTimeMap;
};

export const buildDKCoreDeviceAccessHours: (
  accessHours: AccessHoursType<Date>,
) => DKCoreDeviceAccessHoursType = accessHours => Object.entries(accessHours).reduce(
    (acc, [day, { startTime, endTime }]) => {
      acc![day] = {
        startTime: convertTo24HrTimeNum(startTime),
        endTime:
          convertTo24HrTimeNum(endTime) === 0
            ? 2400
            : convertTo24HrTimeNum(endTime),
      };
      return acc;
    },
    {} as any,
  );
